import { ReactElement, Suspense, SyntheticEvent } from 'react';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { Competition } from 'interfaces/sportsbook-data-levels';
import { EventCollapse } from 'components/EventCollapse';
import { GlobalIcon } from 'components/GlobalIcon';
import { MatchCardInlineSkeleton } from 'components/MatchCardInline/Skeleton';
import { MatchCardInlineUSASkeleton } from 'components/Shared/MatchCardInlineUSA/__partials/Skeleton';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';

const { MatchCardInline } = lazy(
  () =>
    import(
      /* webpackChunkName: "match-card-in-line" */ 'components/MatchCardInline'
    )
);

const { MatchCardInlineUSA } = lazy(
  () =>
    import(
      /* webpackChunkName: "match-card-in-line-usa" */ 'components/Shared/MatchCardInlineUSA'
    )
);

type TCompetitionItemCollapseProps = {
  comp: Competition;
  regionAlias: string;
  sportInfo: {
    name: string;
    alias: string;
  };
  handleMatchCardClick: (
    e: SyntheticEvent,
    region: string,
    competitionId: number,
    gameId: number
  ) => void;
  isFirstCompetition: boolean;
  isBoostedOdd?: boolean;
};

export const CompetitionItemCollapse = ({
  comp,
  regionAlias,
  sportInfo,
  handleMatchCardClick,
  isFirstCompetition,
  isBoostedOdd
}: TCompetitionItemCollapseProps): ReactElement => {
  const configs = useSportsbookConfigs();

  const collapseDefaultActive =
    !Number(SpringConfigs.SPORT_COMPETITION_IS_COMPACT) && isFirstCompetition;

  return (
    <EventCollapse
      key={comp.id}
      defaultActive={collapseDefaultActive}
      headerClassName="regCompCollapseHeader"
      wrapperClassName="regCompCollapseWrapper"
      title={
        <span className="sportCollapseTitle">
          <GlobalIcon name={regionAlias} lib="flags" size={EIconSize._20} />
          {comp.name}
        </span>
      }
    >
      <div>
        {sortByOrder(Object.values(comp.game || {}), 'start_ts').map(game =>
          SpringConfigs.IS_AMERICAN_LAYOUT || +configs.spbLayoutType === 1 ? (
            <Suspense fallback={<MatchCardInlineUSASkeleton />} key={game.id}>
              <MatchCardInlineUSA
                game={game}
                competitionName={comp.name}
                sportAlias={sportInfo.alias}
                eventType="prematch"
                comp={{ id: comp.id }}
                showGameTime
                externalRedirect={e =>
                  handleMatchCardClick(e, regionAlias, comp.id, game.id)
                }
                includeRedirect={false}
                withMoreWagesWrapper
                showFavoriteButton
                showBBIcon
                detailedTeamsInfo
              />
            </Suspense>
          ) : (
            <Suspense
              fallback={<MatchCardInlineSkeleton isBoostedOdd={isBoostedOdd} />}
              key={game.id}
            >
              <MatchCardInline
                game={game}
                currentSport={sportInfo.alias}
                competitionName={comp.name}
                sportName={sportInfo.name}
                onClick={e =>
                  handleMatchCardClick(
                    e as SyntheticEvent,
                    regionAlias,
                    comp.id,
                    game.id
                  )
                }
              />
            </Suspense>
          )
        )}
      </div>
    </EventCollapse>
  );
};
